import React, { useEffect, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';
import {
  Container,
  Header,
  Segment,
  Card,
  Icon,
  Loader,
  Table,
  Label,
  Message,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';

const STATUS_COLORS = {
  draft: 'yellow',
  in_review: 'orange',
  effective: 'green',
  superseded: 'grey',
  obsolete: 'red',
};

export default function QmsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guest users only see external standards
  const token = useSelector((state) => state.userSession?.token);
  const userId = token ? jwtDecode(token).sub : '';
  const isGuest = userId === 'bucharest26';

  useEffect(() => {
    fetchApi(`${getApiPath()}/@qms/dashboard`)
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (isGuest) return <Redirect to="/qms/external" />;

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading QMS dashboard..." />
      </Container>
    );
  }
  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  const totalDocs = (data.by_status || []).reduce(
    (sum, r) => sum + r.count,
    0,
  );

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title="Quality Management System" />
      <Link to="/">&larr; Home</Link>

      <div className="polyglot-page-header">
        <h1>Quality Management System</h1>
        <p className="subtitle">ISO 13485 Document Control Dashboard</p>
      </div>

      {/* Status overview cards */}
      <Card.Group itemsPerRow={4} stackable style={{ marginBottom: '1.5em' }}>
        <Card>
          <Card.Content>
            <Card.Header>{totalDocs}</Card.Header>
            <Card.Meta>Controlled Documents</Card.Meta>
          </Card.Content>
        </Card>
        {(data.by_status || []).map((s) => (
          <Card key={s.status}>
            <Card.Content>
              <Label
                color={STATUS_COLORS[s.status] || 'grey'}
                style={{ float: 'right' }}
              >
                {s.count}
              </Label>
              <Card.Header style={{ textTransform: 'capitalize' }}>
                {s.status.replace('_', ' ')}
              </Card.Header>
            </Card.Content>
          </Card>
        ))}
        <Card>
          <Card.Content>
            <Card.Header>{data.external_documents_count}</Card.Header>
            <Card.Meta>External Standards</Card.Meta>
          </Card.Content>
        </Card>
      </Card.Group>

      {/* Reconciliation warning */}
      {data.reconciliation &&
        data.reconciliation.unregistered_count > 0 && (
          <Message warning icon style={{ marginBottom: '1.5em' }}>
            <Icon name="exclamation triangle" />
            <Message.Content>
              <Message.Header>
                {data.reconciliation.unregistered_count} Unregistered Document
                {data.reconciliation.unregistered_count !== 1 ? 's' : ''}
              </Message.Header>
              <p>
                Knowledge Base documents matching document ID patterns were
                found that are not in the controlled document register.{' '}
                <Link to="/qms/register/unregistered">
                  Review and register &rarr;
                </Link>
              </p>
            </Message.Content>
          </Message>
        )}

      {/* Navigation links */}
      <Segment>
        <Card.Group itemsPerRow={3} stackable>
          <Card as={Link} to="/qms/register" color="blue">
            <Card.Content>
              <Icon
                name="clipboard list"
                size="big"
                style={{ float: 'right', color: '#2185d0' }}
              />
              <Card.Header>Document Register</Card.Header>
              <Card.Description>
                Browse, search, and manage all controlled documents.
              </Card.Description>
            </Card.Content>
          </Card>
          {/* DR-2026-297 — placed first among the cards because it is the
              daily entry point: it is the only one that answers "what needs me
              now" rather than "what exists". */}
          <Card as={Link} to="/qms/review-queue" color="purple">
            <Card.Content>
              <Icon
                name="tasks"
                size="big"
                style={{ float: 'right', color: '#a333c8' }}
              />
              <Card.Header>Review Queue</Card.Header>
              <Card.Description>
                Findings from the QMS detectors awaiting your judgement.
              </Card.Description>
            </Card.Content>
          </Card>
          {/* DR-2026-352 v0.5.0 — the failure-mode register: what recurs, who catches it,
              and whether the control that should catch it next still exists. */}
          <Card as={Link} to="/qms/failure-modes" color="black">
            <Card.Content>
              <Icon
                name="sitemap"
                size="big"
                style={{ float: 'right', color: '#1b1c1d' }}
              />
              <Card.Header>Failure Modes</Card.Header>
              <Card.Description>
                The register's failure modes: occurrences, who caught them, and their
                controls' existence.
              </Card.Description>
            </Card.Content>
          </Card>
          <Card as={Link} to="/qms/reviews" color="orange">
            <Card.Content>
              <Icon
                name="calendar check"
                size="big"
                style={{ float: 'right', color: '#f2711c' }}
              />
              <Card.Header>Review Schedule</Card.Header>
              <Card.Description>
                Upcoming and overdue document reviews.
              </Card.Description>
            </Card.Content>
          </Card>
          <Card as={Link} to="/qms/external" color="teal">
            <Card.Content>
              <Icon
                name="book"
                size="big"
                style={{ float: 'right', color: '#00b5ad' }}
              />
              <Card.Header>External Standards</Card.Header>
              <Card.Description>
                Track referenced standards and regulations.
              </Card.Description>
            </Card.Content>
          </Card>
          <Card as={Link} to="/qms/issues" color="red">
            <Card.Content>
              <Icon
                name="bug"
                size="big"
                style={{ float: 'right', color: '#db2828' }}
              />
              <Card.Header>Issue Tracker</Card.Header>
              <Card.Description>
                Track problem reports, investigations, and resolutions.
              </Card.Description>
            </Card.Content>
          </Card>
          <Card as={Link} to="/qms/recipients" color="violet">
            <Card.Content>
              <Icon
                name="mail"
                size="big"
                style={{ float: 'right', color: '#a333c8' }}
              />
              <Card.Header>Email Recipients</Card.Header>
              <Card.Description>
                Manage the shared recipient registry for emailing KB documents.
              </Card.Description>
            </Card.Content>
          </Card>
          <Card as={Link} to="/docs" color="green">
            <Card.Content>
              <Icon
                name="archive"
                size="big"
                style={{ float: 'right', color: '#21ba45' }}
              />
              <Card.Header>Knowledge Base</Card.Header>
              <Card.Description>
                Browse all project documentation, guides, and reference
                material.
              </Card.Description>
            </Card.Content>
          </Card>
          <Card as={Link} to="/qms/training" color="purple">
            <Card.Content>
              <Icon
                name="graduation cap"
                size="big"
                style={{ float: 'right', color: '#a333c8' }}
              />
              <Card.Header>Training</Card.Header>
              <Card.Description>
                Competence certification through hands-on practice of document
                control workflows.
              </Card.Description>
            </Card.Content>
          </Card>
          <Card as={Link} to="/qms/register/unregistered" color="red">
            <Card.Content>
              <Icon
                name="search"
                size="big"
                style={{ float: 'right', color: '#db2828' }}
              />
              <Card.Header>Reconciliation</Card.Header>
              <Card.Description>
                Detect and register documents from the Knowledge Base.
              </Card.Description>
            </Card.Content>
          </Card>
        </Card.Group>
      </Segment>

      {/* Overdue reviews */}
      {data.overdue_reviews && data.overdue_reviews.length > 0 && (
        <>
          <Header as="h3" color="red">
            <Icon name="warning sign" /> Overdue Reviews
          </Header>
          <Table compact celled color="red">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Document</Table.HeaderCell>
                <Table.HeaderCell>Title</Table.HeaderCell>
                <Table.HeaderCell>Due</Table.HeaderCell>
                <Table.HeaderCell>Days Overdue</Table.HeaderCell>
                <Table.HeaderCell>Owner</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.overdue_reviews.map((r) => (
                <Table.Row key={r.document_id}>
                  <Table.Cell>
                    <Link to={`/qms/register/${encodeURIComponent(r.document_id)}`}>
                      {r.document_id}
                    </Link>
                  </Table.Cell>
                  <Table.Cell>{r.title}</Table.Cell>
                  <Table.Cell>{r.next_review_date}</Table.Cell>
                  <Table.Cell>
                    <Label color="red">{r.days_overdue}</Label>
                  </Table.Cell>
                  <Table.Cell>{r.owner}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}

      {/* Recent activity */}
      <Header as="h3">Recent Activity</Header>
      <Table compact celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Document</Table.HeaderCell>
            <Table.HeaderCell>Title</Table.HeaderCell>
            <Table.HeaderCell>Action</Table.HeaderCell>
            <Table.HeaderCell>By</Table.HeaderCell>
            <Table.HeaderCell>When</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(data.recent_activity || []).map((a) => (
            <Table.Row key={a.id}>
              <Table.Cell>
                <Link to={`/qms/register/${encodeURIComponent(a.document_id)}`}>
                  {a.document_id}
                </Link>
              </Table.Cell>
              <Table.Cell>{a.title}</Table.Cell>
              <Table.Cell>
                <Label size="tiny">{a.action}</Label>
                {a.to_status && (
                  <Label
                    size="tiny"
                    color={STATUS_COLORS[a.to_status] || 'grey'}
                    style={{ marginLeft: '0.3em' }}
                  >
                    {a.to_status}
                  </Label>
                )}
              </Table.Cell>
              <Table.Cell>{a.performed_by}</Table.Cell>
              <Table.Cell>
                {new Date(a.performed_at).toLocaleDateString()}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Container>
  );
}
